import { type LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { type LinearConfigurationDTO } from "~/features/configurator/Linear/dtos";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import {
  type CarriageConfiguratorApiData,
  type ConfiguratorApiCall,
  prepareCommonConfiguratorData,
} from "~/features/configurator/common/commonConfiguratorApi";
import { getConfiguratorFamilyFromProductCode } from "~/domains/configurator/types/ConfiguratorType";
import { FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES } from "~/features/configurator/Carriage/carriageConfiguratorConstants";
import { type PriceInfo } from "~/domains/pricing/api/PriceInfo";
import instance from "~/utils/i18n";
import { XML_TRANSLATION_SHEETS } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";

type CarriageConfiguratorApiCall = ConfiguratorApiCall &
  Required<Pick<ConfiguratorApiCall, "carriagesConfiguratorData">>;

export function prepareCommonCarriageData(
  linearState: LinearConfiguratorState,
  priceInfo?: PriceInfo | null,
): CarriageConfiguratorApiCall {
  const data = prepareCommonConfiguratorData(linearState, priceInfo);

  const configuratorFamily = getConfiguratorFamilyFromProductCode(linearState.productCode);
  const t = (key: string) =>
    instance.t(key, { ns: XML_TRANSLATION_SHEETS[linearState.type!].sheetName, context: configuratorFamily });

  const mainKmat = getMainKmat({ kmats: linearState.kmats });
  const enableConfigureCarriageValues = mainKmat?.cstics[FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES]?.value === "true";

  const carriageData: CarriageConfiguratorApiData = {
    // currentStepNumber is 1 based
    currentStepNumber: linearState.currentStep + 1,
    highestVisitedStepNumber: linearState.highTideStep + 1,
    enableConfigureCarriageValues,
    description: linearState.description,
    mediaUrl: t(linearState.productImageUrl),
  };

  data.carriagesConfiguratorData = carriageData;

  return data as CarriageConfiguratorApiCall;
}

const apiUrl = resolveApiUrl("/carriages/getconfig");

export function sendCarriage(data: ConfiguratorApiCall): Promise<LinearConfigurationDTO> {
  return fetch(apiUrl, {
    body: JSON.stringify(data),
    cache: "no-cache",
    credentials: "same-origin",
    method: "POST",
    mode: "cors",
    headers: {
      dataType: "json",
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    redirect: "follow",
  }).then((result) => {
    if (result.ok) {
      return result.json();
    } else {
      console.error(`Error sending linear update: ${result.status}`);
      throw new Error(result.status + "");
    }
  });
}
